import classNames from 'classnames';
import React from 'react';
import s from './authorizeButton.module.scss';

interface Props {
  /** A sign-in is in flight. The button shows it and refuses a second press. */
  busy: boolean;
  onClick: () => void;
  /**
   * Google's branded image button instead of a text button. Google's brand
   * rules want their artwork used as-is, so this variant carries no label and
   * puts the busy status beside the button rather than inside it.
   */
  google?: boolean;
  className?: string;
  /** What is shown while `busy`. Defaults to "Signing in…". */
  busyLabel?: string;
  children?: React.ReactNode;
}

/**
 * A provider sign-in button - every one of them, so they all behave alike.
 *
 * Sign-in is never instant. MSAL opens a blank popup and then spends a couple
 * of seconds fetching authority metadata before anything appears in it;
 * Dropbox derives a PKCE challenge first; GIS opens its popup and then waits
 * on Google; on iOS the whole page is about to navigate away. In every case
 * the button used to look exactly the same before and after the press, which
 * invites a second press - and a second popup, which MSAL rejects as
 * interaction_in_progress and gisClient as "already in progress".
 *
 * So: one look for "pressed and working", and no clicks while it is.
 */
export default function AuthorizeButton({
  busy,
  onClick,
  google,
  className,
  busyLabel,
  children,
}: Props) {

  const status = (
    <>
      {busyLabel || 'Signing in'}
      <span className={s.dots} aria-hidden="true">
        <span>.</span><span>.</span><span>.</span>
      </span>
    </>
  );

  if (google) {
    return (
      <span className={s.googleWrap}>
        <button
          type="button"
          className={classNames(s.google, className, { [s.busy]: busy })}
          disabled={busy}
          aria-busy={busy}
          aria-label="Sign in with Google"
          onClick={busy ? undefined : onClick}
        >
        </button>
        {busy &&
          <span className={s.status} role="status">{status}</span>
        }
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classNames('button', className, { [s.busy]: busy })}
      disabled={busy}
      aria-busy={busy}
      onClick={busy ? undefined : onClick}
    >
      {busy ? status : children}
    </button>
  );
}
